/**
 * Google Analytics 4 (gtag.js) plugin.
 * Measurement ID configured via NUXT_PUBLIC_GA_MEASUREMENT_ID. Initializes only after analytics
 * consent is granted (see composables/useAnalyticsConsent.ts) and no-ops safely otherwise.
 */

import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import {
  getAnalyticsConsentBlockedReason,
  hasAnalyticsConsent,
  subscribeAnalyticsConsentChanges,
} from '../../composables/useAnalyticsConsent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // Ensure window exists (guard against SSR/hydration issues)
  if (typeof window === 'undefined') return;

  const config = useRuntimeConfig();
  const measurementId = typeof config.public.gaMeasurementId === 'string'
    ? config.public.gaMeasurementId
    : '';

  if (!measurementId) return;

  // Guard against duplicate initialization during HMR.
  if (nuxtApp._ga4Initialized) return;

  let isInitialized = false;

  function loadGtagScript(id: string): void {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="https://www.googletagmanager.com/gtag/js?id=${id}"]`
    );

    if (existingScript) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.addEventListener('error', () => {
      console.warn('[GA4] analytics_script_load_failed');
    }, { once: true });
    document.head.appendChild(script);
  }

  function initializeGa4(): void {
    if (isInitialized || !hasAnalyticsConsent()) {
      if (!hasAnalyticsConsent()) {
        console.warn(`[GA4] analytics_blocked: ${getAnalyticsConsentBlockedReason()}`);
      }
      return;
    }

    // Create the gtag queue before loading gtag.js, as required by Google's setup snippet.
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function gtag(): void {
        window.dataLayer.push(arguments);
      };
    }

    window.gtag('consent', 'default', {
      'analytics_storage': 'granted',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
    });

    loadGtagScript(measurementId);
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    isInitialized = true;
    nuxtApp._ga4Initialized = true;
  }

  initializeGa4();

  subscribeAnalyticsConsentChanges((granted) => {
    if (granted) {
      initializeGa4();
      return;
    }

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
      });
    }
  });
});
